const ERR_NODE_IN_LIST = 'Node already exists in another list!';
/**
 * A ListNode represents a single item within a list.
 *
 * @typeparam T The type of the value within the ListNode. Defaults to any.
 */
export class ListNode {
    /**
     * @param value - The value contained within the node.
     */
    constructor(value) {
        /**
         * The id of the list that this node belongs to, or null if it is not inside
         * a list.
         */
        this.listId = null;
        /**
         * The key for this node.
         *
         * Normally unused but required by the LRU cache. This is added to the object
         * in order to optimize for object shape in V8-like engines.
         */
        this.key = '';
        /**
         * The node in the list before this particular node.
         */
        this.prev = null;
        /**
         * The node in the list after this particular node.
         */
        this.next = null;
        /**
         * The actual value contained in the node.
         */
        this.value = null;
        this.value = value;
    }
}
export class LinkedList {
    constructor() {
        /**
         * An identifier used to determine whether a node is from this list.
         */
        this._id = {};
        /**
         * The length of the list.
         */
        this.length = 0;
        /**
         * The first node in the list.
         */
        this.first = null;
        /**
         * The last node in the list.
         */
        this.last = null;
    }
    /**
     * Appends a `ListNode` to the end of the list.
     *
     * @param newNode - The node to append.
     * @return The new length of the list.
     */
    append(newNode) {
        if (newNode.listId) {
            throw new Error(ERR_NODE_IN_LIST);
        }
        newNode.listId = this._id;
        if (!this.first) {
            // This list is empty.
            this.first = newNode;
            this.last = newNode;
        }
        else if (this.last) {
            newNode.prev = this.last;
            newNode.next = null;
            this.last.next = newNode;
            this.last = newNode;
        }
        return ++this.length;
    }
    /**
     * Inserts a `ListNode` after the provided `node`.
     *
     * @param node - A node within the list. The `newNode` argument will be
     *   appended after this node.
     * @param newNode - The node to insert.
     * @return The new length of the list.
     */
    insertAfter(node, newNode) {
        if (newNode.listId) {
            throw new Error(ERR_NODE_IN_LIST);
        }
        newNode.listId = this._id;
        newNode.prev = node;
        newNode.next = node.next;
        if (node.next) {
            node.next.prev = newNode;
        }
        node.next = newNode;
        if (newNode.prev === this.last) {
            this.last = newNode;
        }
        return ++this.length;
    }
    /**
     * Removes a `ListNode` from the list.
     *
     * @param node - The node to remove.
     * @return True if the node was removed, false otherwise.
     */
    remove(node) {
        if (!this.length || node.listId !== this._id) {
            return false;
        }
        else if (this.length > 1) {
            if (node.prev) {
                node.prev.next = node.next;
            }
            if (node.next) {
                node.next.prev = node.prev;
            }
            if (node === this.first) {
                this.first = node.next;
            }
            else if (node === this.last) {
                this.last = node.prev;
            }
        }
        else {
            this.first = null;
            this.last = null;
        }
        node.listId = null;
        node.prev = null;
        node.next = null;
        this.length--;
        return true;
    }
}
//# sourceMappingURL=linkedlist.js.map